/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// Component imports
import { Col, Form, Input, Radio, Row, Select, Space, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import VariableSelectItem from '../../../UI/VariableSelectItem';

// Misc imports
import { VARIABLE_TYPES } from '../../../../constants/main';
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';
import groupBy from '../../../../utils/array/groupBy';

// Constant declarations
const { Text } = Typography;
const DEFAULT_CATEGORY = 'Other';

const VariableNodeAAForm = ({
  form,
  responseIndex,
  canEdit
}) => {
  const { t } = useTranslation();
  const { orgId, tenantId } = useParams();

  const { data: storyVariables, isFetching: isFetchingStoryVariables } = getCurrentQueryState('fetchStoryVariables', { orgId, tenantId });

  const [localVariables, setLocalVariables] = useState([]); // Only for use within the Variable node, other nodes should access the storyVariables directly

  // Whenever the story variables change, update the local variables
  useEffect(() => {
    const validVariables = [...storyVariables.filter(variable => variable.type !== VARIABLE_TYPES.STATIC)];
    setLocalVariables(validVariables);
  }, [storyVariables]);

  const updateVariables = (newVariableName) => {
    if (newVariableName && storyVariables.filter(variable => variable.name === newVariableName).length === 0) {
      setLocalVariables([...new Set([...storyVariables.filter(variable => variable.type !== VARIABLE_TYPES.STATIC), { name: newVariableName, type: '' }])]);
    }
  };

  return (
    <Fragment>
      <Row className="mb-2">
        <Col xs={24}>
          <Text type="secondary" strong className="fs-sm">
            {t('Define values, which can be used by the Flow control node to direct the end-user to different paths of the flow, depending on the values of the specified variables.')}
          </Text>
        </Col>
      </Row>

      <Row className="mb-1">
        <Col xs={24}>
          <Form.Item
            name={[responseIndex, 'variable', 'key']}
            label={
              <Space>
                {t('Variable Name')}

                <Tooltip
                  placement="right"
                  title={t('Select a variable or type to create custom.')}
                >
                  <QuestionCircleOutlined className="text-secondary" />
                </Tooltip>
              </Space>
            }
            rules={[
              {
                required: true,
                message: t('Key cannot be blank.')
              }]
            }
            className="mb-2"
          >
            {
              storyVariables && localVariables ? (
                <Select
                  size="large"
                  showSearch
                  placeholder={t('Type / select variable')}
                  // When the user types a new variable name, add it to the list of local variables
                  onSearch={updateVariables}
                  onBlur={(e) => updateVariables(e.target.value)}
                  loading={isFetchingStoryVariables}
                  disabled={!canEdit}
                  filterOption={(input, option) => {
                    // Filter Option iterates through the options, find the current option to get the name and the description data
                    const currentVariable = localVariables.find((variable) => variable.name === option.key);
                    return currentVariable?.name.toLowerCase().includes(input.toLowerCase()) || currentVariable?.description?.toLowerCase().includes(input.toLowerCase());
                  }}
                  getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
                >
                  {
                    Object.entries(groupBy(localVariables?.filter((item, index, self) => self.findIndex(t => t.name === item.name) === index),
                      item => item.type || DEFAULT_CATEGORY))
                      .map(([groupName, items]) => {
                        // Apply filtering before rendering the group
                        const filteredItems = items.filter((variable) => {
                          return ![VARIABLE_TYPES.QUESTION, VARIABLE_TYPES.SYSTEM, VARIABLE_TYPES.LIVECHAT, VARIABLE_TYPES.CONTACT, VARIABLE_TYPES.CSAT].includes(variable.type);
                        });

                        // Only render the group if it has items after filtering
                        return filteredItems.length > 0 ? (
                          <Select.OptGroup key={groupName} label={t(groupName || DEFAULT_CATEGORY, { context: 'variable type' })}>
                            {
                              filteredItems.map(variable => (
                                <Select.Option
                                  key={variable.name}
                                  value={variable.name}
                                  name={variable.name}
                                >
                                  <VariableSelectItem
                                    variable={variable}
                                    showDescription={true}
                                    showActivity={false}
                                    truncateCount={0}
                                    truncateDescriptionCount={45}
                                  />
                                </Select.Option>
                              ))
                            }
                          </Select.OptGroup>
                        ) : null;
                      })
                  }
                </Select>
              ) : null
            }
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item
            name={[responseIndex, 'variable', 'value']}
            rules={[
              {
                required: true,
                message: t('Value cannot be blank.')
              }]
            }
            label={
              <Space>
                {t('Variable Value')}

                <Tooltip
                  placement="right"
                  title={t('Type the value for the variable.')}
                >
                  <QuestionCircleOutlined className="text-secondary" />
                </Tooltip>
              </Space>
            }
          >
            <Input disabled={!canEdit} size="large" placeholder={t('Type value')} />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <Form.Item
            name={[responseIndex, 'variable', 'type']}
            rules={[
              {
                required: true,
                message: t('Type cannot be blank.')
              }]
            }
            label={
              <Row>
                <Col span={24}>
                  <Text>
                    {t('Variable Type')}
                  </Text>
                </Col>
                <Col span={24} className="mb-2">
                  <Text type="secondary" className="fs-sm mr-3">
                    {t('Choose how to store this variable.')}
                  </Text>
                </Col>
              </Row>
            }
          >
            <Radio.Group disabled={!canEdit}>
              <Radio
                key="session"
                value="SESSION"
              >
                {t('Session variable')}
              </Radio>

              <Radio
                key="contact"
                value="CONTACT"
              >
                {t('Contact variable')}
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
    </Fragment>
  );
};

VariableNodeAAForm.propTypes = {
  form: PropTypes.object.isRequired,
  responseIndex: PropTypes.number.isRequired,
  canEdit: PropTypes.bool.isRequired
};

export default VariableNodeAAForm;
